import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiBook } from 'react-icons/fi';
import { useAuthContext } from '../auth-config';
import { authService } from '../auth-config/auth-service';
import { encryptId } from '../utils/crypto';
import { FirstTimeOnboardingModal } from '../components/FirstTimeOnboardingModal';

const DashboardPage: React.FC = () => {
  const { user } = useAuthContext();

  const firstName = user?.firstName || 'Masti';
  const lastName = user?.lastName || 'Manch';
  const initials = ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() || 'MM';

  const board = user?.attributes?.board || user?.attributes?.['custom:board'] || '';
  const standard = user?.attributes?.standard || user?.attributes?.['custom:standard'] || '';
  const studentInfo = [standard, board].filter(Boolean).join(', ') || 'Student';

  const [subjects, setSubjects] = useState<{ name: string; code: string }[]>([]);
  const [books, setBooks] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<{ name: string; code: string } | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('trial');
  const [loadingSubjects, setLoadingSubjects] = useState<boolean>(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);
  const [userIdNumeric, setUserIdNumeric] = useState<number>(0);
  const [inProgressVideos, setInProgressVideos] = useState<any[]>([]);
  const [loadingInProgress, setLoadingInProgress] = useState<boolean>(true);
  const lastFetchedId = React.useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id || lastFetchedId.current === user.id) return;
    lastFetchedId.current = user.id;

    const fetchUserSubjects = async () => {
      try {
        setLoadingSubjects(true);

        const token = await authService.getAccessToken();
        if (!token) return;

        // 1. Fetch user profile from DB to get subscription status, board, standard, and medium
        const profileRes = await fetch(`/api/users/${user.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!profileRes.ok) throw new Error('Failed to fetch user profile');
        const profileData = await profileRes.json();
        const status = profileData.subscriptionStatus || 'trial';
        setSubscriptionStatus(status);

        if (!profileData.studentAgreementAccepted || !profileData.privacyPolicyAccepted || !profileData.videoDisclaimerAccepted) {
          setShowOnboardingModal(true);
          setUserIdNumeric(profileData.id);
        }

        const activeBoard = profileData.board || board;
        const activeStandard = profileData.standard || standard;
        const activeMedium = profileData.medium || user?.attributes?.medium || user?.attributes?.['custom:medium'] || 'English';

        // 2. Fetch standards to get standard ID and list of books
        if (activeBoard && activeStandard) {
          const standardsRes = await fetch(`/api/student/register/standards?board=${encodeURIComponent(activeBoard)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (standardsRes.ok) {
            const standards = await standardsRes.json();
            const stdObj = standards.find((s: any) => s.name === activeStandard);
            if (stdObj) {
              // Fetch subjects if trial
              if (status === 'trial') {
                const subjectsRes = await fetch(`/api/student/register/subjects?board=${encodeURIComponent(activeBoard)}&standardId=${stdObj.id}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                if (subjectsRes.ok) {
                  const allSubjects = await subjectsRes.json();
                  setSubjects(allSubjects.map((sub: any) => ({
                    name: sub.name,
                    code: sub.code || sub.name
                  })));
                }
              }

              // Fetch all books matching board, standard, and medium
              const booksRes = await fetch(`/api/student/books?board=${encodeURIComponent(activeBoard)}&standardId=${stdObj.id}&medium=${encodeURIComponent(activeMedium)}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              if (booksRes.ok) {
                const allBooks = await booksRes.json();
                setBooks(allBooks);
              }
            }
          }
        }

        // 3. If subscribed, parse subjects from profileData
        if (status !== 'trial') {
          const userSubjs = profileData.subjects || [];
          const parsed = userSubjs.map((s: string) => {
            const parts = s.split(' - ');
            const name = parts[parts.length - 1] || s;
            return {
              name: name,
              code: name
            };
          });
          setSubjects(parsed);
        }

        // 4. Fetch in-progress videos
        try {
          const inProgressRes = await fetch('/api/student/videos/in-progress', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (inProgressRes.ok) {
            const data = await inProgressRes.json();
            setInProgressVideos(data || []);
          }
        } catch (err) {
          console.error('Error fetching in-progress videos:', err);
        } finally {
          setLoadingInProgress(false);
        }
      } catch (err) {
        console.error('Error fetching subjects/books:', err);
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchUserSubjects();
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-white">
      {/* BEGIN: UserGreeting */}
      <div className="bg-[#f7f9fa] border-b border-[#d1d7dc]">
        <section className="max-w-[1340px] mx-auto px-6 py-8 flex items-center gap-6" data-purpose="user-welcome">
          <div className="w-20 h-20 rounded-full bg-black text-white flex items-center justify-center font-bold text-2xl">
            {initials}
          </div>
          <div>
            <h1 className="text-3xl font-bold mb-1">Welcome, {firstName} {lastName}</h1>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-bold">{studentInfo}</span>
              <Link className="text-[#5624d0] font-bold underline" to="/profile">Edit profile</Link>
            </div>
          </div>
        </section>
      </div>
      {/* END: UserGreeting */}

      <main className="max-w-[1340px] mx-auto px-6 py-8">

        {/* BEGIN: StreakCard */}
        <section className="streak-card rounded-lg p-8 mb-16 flex items-center justify-between" data-purpose="learning-streak">
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2">Start a new streak</h2>
            <p className="text-[#6a6f73]">You'll always be glad you made time for learning.</p>
          </div>
          <div className="flex items-center gap-12 pr-4">
            {/* Streak Info */}
            <div className="flex items-center gap-4">
              <div className="text-[#6a6f73]">
                <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M13.5 1.5s4.5 4.715 4.5 9.3c0 3.315-2.685 6-6 6s-6-2.685-6-6c0-2.31 1.095-4.47 2.82-5.91L11.1 3.42C8.01 4.74 6 7.8 6 11.25c0 4.41 3.585 8.01 8.01 8.01s8.01-3.6 8.01-8.01c0-4.71-3.66-8.79-8.52-9.75z"></path>
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold">0 <span className="text-lg font-normal">weeks</span></div>
                <div className="text-sm text-[#6a6f73]">Current streak</div>
              </div>
            </div>
            {/* Radial Progress Circle */}
            <div className="flex items-center gap-4 border-l pl-12 border-[#d1d7dc] relative">
              <div className="relative w-16 h-16">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="32" cy="32" fill="transparent" r="28" stroke="#d1d7dc" strokeWidth="6"></circle>
                  <circle cx="32" cy="32" fill="transparent" r="28" stroke="#1c8d73" strokeDasharray="175" stroke-dashoffset="175" strokeLinecap="round" strokeWidth="6"></circle>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full border-4 border-[#1c8d73]"></div>
                </div>
              </div>
              <div className="text-sm font-bold space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                  <span>0/0 course min</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-600"></span>
                  <span>2/2 visit</span>
                </div>
                <div className="text-[#6a6f73] font-normal">Jun 21 - 27</div>
              </div>
              {/* Info Icon */}
              <div className="absolute top-0 -right-4">
                <svg className="w-4 h-4 text-[#6a6f73]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                </svg>
              </div>
            </div>
          </div>
        </section>
        {/* END: StreakCard */}

        {/* BEGIN: PickUpSection */}
        {!loadingInProgress && inProgressVideos.length > 0 && (
          <section data-purpose="course-resume-section" className="mb-16">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Pick up where you left off</h2>
            </div>
            <div className="relative group">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {inProgressVideos.slice(0, 3).map((video) => {
                  const percent = Math.min(100, Math.round((video.lastPositionSeconds / (video.durationSeconds || 1)) * 100));
                  
                  return (
                    <Link
                      to={`/courses/${encryptId(video.bookId)}/${encryptId(video.videoId)}`}
                      key={video.videoId}
                      className="bg-white border border-[#d1d7dc] rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer flex flex-col justify-between h-40"
                    >
                      <div className="flex p-4 gap-4">
                        <div className="relative w-28 h-20 shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                          {video.thumbnailUrl ? (
                            <img
                              alt={video.topicName}
                              className="w-full h-full object-cover"
                              src={video.thumbnailUrl}
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-xs">
                              Video
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/15">
                            <div className="bg-white/90 rounded-full p-1.5 shadow-md">
                              <svg className="w-4 h-4 text-[#2d2f31] fill-current" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"></path></svg>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col justify-between overflow-hidden">
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 truncate">{video.subjectCode} • {video.chapterName}</p>
                            <h3 className="text-sm font-bold leading-snug line-clamp-2 text-gray-800">{video.topicName}</h3>
                          </div>
                          <p className="text-[11px] text-gray-500 font-medium mt-1">
                            {Math.floor(video.lastPositionSeconds / 60)}m left of {Math.floor(video.durationSeconds / 60)}m
                          </p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 h-1.5">
                        <div className="bg-blue-600 h-full transition-all duration-300" style={{ width: `${percent}%` }}></div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
        {/* END: PickUpSection */}

        {/* BEGIN: MySubjectsSection */}
        <section className="mt-16" data-purpose="subscribed-subjects">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <span className="cursor-pointer hover:underline" onClick={() => setSelectedSubject(null)}>My Subjects</span>
                {selectedSubject && (
                  <>
                    <span>&gt;</span>
                    <span className="text-gray-900 font-medium">{selectedSubject.name}</span>
                  </>
                )}
              </div>
              <h2 className="text-2xl font-bold">
                {selectedSubject ? `${selectedSubject.name} Books` : 'My Subjects'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedSubject
                  ? `Select a book to browse lessons for ${selectedSubject.name}`
                  : (subscriptionStatus === 'trial'
                    ? 'All subjects are active under your 7-day free trial'
                    : 'Your subscribed academic subjects')}
              </p>
            </div>
            {selectedSubject ? (
              <button
                onClick={() => setSelectedSubject(null)}
                className="text-[#5624d0] hover:text-purple-800 font-bold flex items-center gap-1.5 transition-colors border border-[#d1d7dc] hover:bg-gray-50 px-4 py-2 rounded-xl"
              >
                <span>&larr;</span> Back to Subjects
              </button>
            ) : (
              subscriptionStatus === 'trial' && (
                <span className="bg-orange-100 text-orange-800 text-xs font-bold px-3 py-1.5 rounded-full border border-orange-200">
                  7-Day Free Trial
                </span>
              )
            )}
          </div>

          {loadingSubjects ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="animate-pulse bg-gray-50 border border-gray-200 rounded-2xl p-6 h-36 flex flex-col justify-between">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : selectedSubject ? (
            // Render Books for Selected Subject
            (() => {
              const filteredBooks = books.filter(b =>
                b.subjectName.toLowerCase() === selectedSubject.name.toLowerCase() ||
                b.subjectCode.toLowerCase() === selectedSubject.code.toLowerCase()
              );

              return filteredBooks.length === 0 ? (
                <div className="bg-gray-50 border border-dashed border-gray-300 rounded-2xl p-8 text-center">
                  <FiBook className="mx-auto text-gray-400 w-12 h-12 mb-3" />
                  <p className="text-gray-600 font-medium">No books found for this subject.</p>
                  <p className="text-sm text-gray-400 mt-1">Please contact support if you think this is an error.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {filteredBooks.map((book, idx) => {
                    const themes = [
                      { bg: 'from-blue-50 to-indigo-50/30', border: 'hover:border-blue-500', text: 'text-blue-600', iconBg: 'bg-blue-100' },
                      { bg: 'from-purple-50 to-pink-50/30', border: 'hover:border-purple-500', text: 'text-purple-600', iconBg: 'bg-purple-100' },
                      { bg: 'from-emerald-50 to-teal-50/30', border: 'hover:border-emerald-500', text: 'text-emerald-600', iconBg: 'bg-emerald-100' },
                      { bg: 'from-amber-50 to-orange-50/30', border: 'hover:border-amber-500', text: 'text-amber-600', iconBg: 'bg-amber-100' },
                    ];
                    const theme = themes[idx % themes.length];

                    return (
                      <Link
                        to={`/courses/${encryptId(book.id)}`}
                        key={book.id}
                        className={`bg-gradient-to-br ${theme.bg} border border-[#d1d7dc] rounded-2xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer ${theme.border} group flex flex-col justify-between h-44`}
                      >
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <div className={`w-10 h-10 rounded-xl ${theme.iconBg} flex items-center justify-center ${theme.text} transition-transform group-hover:scale-110`}>
                              <FiBook className="w-5 h-5" />
                            </div>
                            {book.publisher && (
                              <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400 bg-white/70 px-2 py-0.5 rounded-md border border-gray-100">
                                {book.publisher}
                              </span>
                            )}
                          </div>
                          <h3 className="font-bold text-lg text-gray-800 line-clamp-2 leading-snug group-hover:text-purple-700 transition-colors">{book.name}</h3>
                          {book.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{book.description}</p>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-4 pt-2 border-t border-gray-100/50">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          <span>Open textbook</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              );
            })()
          ) : subjects.length === 0 ? (
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-2xl p-8 text-center">
              <FiBook className="mx-auto text-gray-400 w-12 h-12 mb-3" />
              <p className="text-gray-600 font-medium">No subjects found.</p>
              <p className="text-sm text-gray-400 mt-1">Please complete your enrollment or contact support.</p>
            </div>
          ) : (
            // Render Subjects Grid
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {subjects.map((sub, idx) => {
                const themes = [
                  { bg: 'from-blue-50 to-indigo-50/30', border: 'hover:border-blue-500', text: 'text-blue-600', iconBg: 'bg-blue-100' },
                  { bg: 'from-purple-50 to-pink-50/30', border: 'hover:border-purple-500', text: 'text-purple-600', iconBg: 'bg-purple-100' },
                  { bg: 'from-emerald-50 to-teal-50/30', border: 'hover:border-emerald-500', text: 'text-emerald-600', iconBg: 'bg-emerald-100' },
                  { bg: 'from-amber-50 to-orange-50/30', border: 'hover:border-amber-500', text: 'text-amber-600', iconBg: 'bg-amber-100' },
                ];
                const theme = themes[idx % themes.length];

                return (
                  <div
                    key={sub.code + '-' + idx}
                    onClick={() => setSelectedSubject(sub)}
                    className={`bg-gradient-to-br ${theme.bg} border border-[#d1d7dc] rounded-2xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer ${theme.border} group`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-10 h-10 rounded-xl ${theme.iconBg} flex items-center justify-center ${theme.text} transition-transform group-hover:scale-110`}>
                        <FiBook className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-gray-400">{sub.code}</span>
                    </div>
                    <h3 className="font-bold text-lg text-gray-800 line-clamp-2 mb-1">{sub.name}</h3>
                    <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      <span>Browse books</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        {/* END: MySubjectsSection */}
      </main>

      {showOnboardingModal && (
        <FirstTimeOnboardingModal 
          userId={userIdNumeric} 
          onComplete={() => setShowOnboardingModal(false)} 
        />
      )}
    </div>
  );
};

export default DashboardPage;